/**
 * ORGANIZATION GOVERNANCE SETTINGS, AS THE BROWSER SEES THEM.
 *
 * Mirrored verbatim from `functions/src/governance/orgGovernance.ts`. A guard
 * test reads both files and fails when they drift, the same arrangement
 * policyCapabilities.ts already uses.
 *
 * The screen must reach the same answer as the server or the product hides
 * something the server would have served, or shows something it will refuse.
 * The server remains the boundary: nothing here is a security check. It exists
 * so the page does not ask for what it cannot have, and so the settings page
 * can say what a value means without a round trip.
 */

// ============================================================================
// ROLES
// ============================================================================

/**
 * The roles a role set may name. `sysadmin` is deliberately absent: it is
 * always allowed and is never stored, so an organization cannot lock its own
 * System Admin out of something by editing a list.
 */
export const SETTABLE_ROLES = ['viewer', 'author', 'reviewer', 'approver', 'admin'] as const;
export type SettableRole = (typeof SETTABLE_ROLES)[number];

/** Every settable role. The widest a role set gets. */
export const EVERYONE: SettableRole[] = ['viewer', 'author', 'reviewer', 'approver', 'admin'];
/** Everyone who writes or governs documents. Excludes a plain reader. */
export const GOVERNANCE_PARTICIPANTS: SettableRole[] = ['author', 'reviewer', 'approver', 'admin'];

/**
 * Whether a caller holding these roles is inside a role set.
 *
 * `sysadmin` is allowed unconditionally. This is membership, not hierarchy:
 * a set naming `admin` does not admit an approver, because these sets say who
 * may SEE a thing rather than how privileged someone is, and a hospital that
 * names three roles means those three.
 */
export function mayView(roles: readonly string[] | null | undefined, allowed: readonly string[] | null | undefined): boolean {
  if (!Array.isArray(roles)) return false;
  if (roles.includes('sysadmin')) return true;
  if (!Array.isArray(allowed)) return false;
  return roles.some((r) => allowed.includes(r));
}

// ============================================================================
// WHAT IS GOVERNED
// ============================================================================

/**
 * The sections this system owns. The settings collection holds others —
 * branding, auth, appearance — which are not touched, not initialized, not
 * reset and not shown on the governance page.
 */
export const GOVERNED_SECTIONS = ['visibility', 'notifications', 'governance', 'acknowledgements'] as const;
export type GovernedSection = (typeof GOVERNED_SECTIONS)[number];

/**
 * The keys of each governed section, and ONLY these. A section document may
 * hold other fields — `notifications` carries SMTP plumbing that is none of a
 * hospital's business — and every write, reset and initialization is limited
 * to this allow-list so that plumbing is never clobbered or exposed.
 */
export const GOVERNED_KEYS: Record<GovernedSection, readonly string[]> = {
  visibility: [
    'approvalRecordViewRoles',
    'approvalCommentViewRoles',
    'committeeEvidenceViewRoles',
    'versionHistoryViewRoles',
    'retiredPolicyViewRoles',
    'acknowledgementReportViewRoles',
  ],
  notifications: [
    'remindersEnabled',
    'acknowledgementRemindersEnabled',
    'reviewRemindersEnabled',
    'stalledApprovalDigestEnabled',
    'acknowledgementRemindersMandatory',
    'reviewRemindersMandatory',
    'stalledApprovalDigestMandatory',
    'reviewReminderDays',
    'stalledApprovalAfterDays',
  ],
  governance: [
    'defaultReviewIntervalMonths',
    'enforceReviewDate',
    'requireOwnerOnPublish',
    'requirePolicyNumberOnPublish',
  ],
  acknowledgements: [
    'acknowledgementsEnabled',
    'defaultAcknowledgementDays',
    'escalationEnabled',
    'escalationAfterDaysLate',
  ],
};

/**
 * ONE GOVERNED KEY IS NOT STORED IN ITS SECTION DOCUMENT.
 *
 * `remindersEnabled` lives at `orgs/{orgId}.settings.remindersEnabled` and
 * stays there. It is not hidden — the System Admin sees and sets it as the
 * first control of the Notifications section, and the console writes the same
 * field — but its LOCATION is a query optimisation that has to be kept.
 *
 * The four reminder jobs apply it as a Firestore `where` on the organization
 * query, so an organization with reminder mail off is never fetched at all.
 * Moving it into the section document would mean every job reading every
 * organization's settings every morning to find out it had nothing to do.
 *
 * So the settings layer OWNS the key and routes it. A guard test pins this
 * map, because a key that reads from one place and writes to another is
 * exactly the sort of seam that comes apart quietly.
 */
export const ORG_DOCUMENT_KEYS: Record<string, string> = {
  remindersEnabled: 'settings.remindersEnabled',
};

// ============================================================================
// THE VALUES
// ============================================================================

export type SettingValue = boolean | number | string[] | number[];
export type SectionValues = Record<string, SettingValue>;

/** What StandingDocs recommends. New organizations, and every restore. */
export const RECOMMENDED: Record<GovernedSection, SectionValues> = {
  visibility: {
    // What was approved and by whom is not secret from the people it applies to.
    approvalRecordViewRoles: [...EVERYONE],
    // The one place somebody writes in their own words, and they write bluntly.
    approvalCommentViewRoles: [...GOVERNANCE_PARTICIPANTS],
    // A formal governance fact in the language a hospital already uses.
    committeeEvidenceViewRoles: [...EVERYONE],
    // "What was in force on that date" is not an administrator's question alone.
    versionHistoryViewRoles: [...EVERYONE],
    // No longer in force, and the risk of showing it is that somebody follows it.
    retiredPolicyViewRoles: [...GOVERNANCE_PARTICIPANTS],
    // A named list of employees who have not done something they were told to.
    acknowledgementReportViewRoles: ['admin'],
  },
  notifications: {
    /**
     * OFF, AND THE ORGANIZATION TURNS IT ON WHEN IT IS READY.
     *
     * Creating an organization must not by itself start mailing its staff. A
     * hospital on its first day is importing a library, checking departments,
     * loading employees, working out who each policy should be asked of and
     * running a workflow through to see what happens. Reminder mail during
     * that reaches real addresses about work nobody meant to assign yet, and
     * cannot be recalled.
     *
     * So the decision belongs to the customer's System Admin, who turns it on
     * from their own Settings page when the library is right and the audiences
     * are right. The three categories below are on underneath it, so that
     * switch does the whole job in one click.
     *
     * This is also why the console's creation form no longer asks: a platform
     * operator ticking a box at creation is the same fault under another name.
     */
    remindersEnabled: false,
    acknowledgementRemindersEnabled: true,
    reviewRemindersEnabled: true,
    stalledApprovalDigestEnabled: true,
    // Whether an individual may switch it off. Off means they may.
    acknowledgementRemindersMandatory: false,
    reviewRemindersMandatory: false,
    stalledApprovalDigestMandatory: false,
    reviewReminderDays: [90, 60, 30],
    stalledApprovalAfterDays: 14,
  },
  governance: {
    // publishCore falls back to 12, not the 24 the old settings map claimed.
    defaultReviewIntervalMonths: 12,
    // A no-op in practice: publication always computes a review date.
    enforceReviewDate: true,
    // Not required today. An imported library has policies with no owner.
    requireOwnerOnPublish: false,
    requirePolicyNumberOnPublish: false,
  },
  acknowledgements: {
    acknowledgementsEnabled: true,
    defaultAcknowledgementDays: 30,
    escalationEnabled: true,
    // The job escalates at 7 days overdue. The old map said 14 and nothing read it.
    escalationAfterDaysLate: 7,
  },
};

/**
 * What the product did before any of this existed.
 *
 * Written by the one-time initializer for an organization that already exists,
 * and used as the fallback wherever a key is absent. Installing the settings
 * system must not change an existing organization's behaviour, so these are
 * not aspirations — each one was read off the code that runs today.
 *
 * `remindersEnabled` is absent on purpose: an existing organization's value is
 * whatever its own document already says, which the initializer reads. There
 * is no constant that could be right for every organization.
 */
export const LEGACY: Record<GovernedSection, SectionValues> = {
  visibility: {
    ...RECOMMENDED.visibility,
    // Today every member who sees the record sees every comment.
    approvalCommentViewRoles: [...EVERYONE],
  },
  notifications: {
    acknowledgementRemindersEnabled: true,
    reviewRemindersEnabled: true,
    stalledApprovalDigestEnabled: true,
    acknowledgementRemindersMandatory: false,
    reviewRemindersMandatory: false,
    stalledApprovalDigestMandatory: false,
    reviewReminderDays: [90, 60, 30],
    stalledApprovalAfterDays: 14,
  },
  governance: { ...RECOMMENDED.governance },
  acknowledgements: { ...RECOMMENDED.acknowledgements },
};

/**
 * Every key whose recommendation differs from what the product does today.
 *
 * This is the list of behaviours that change for an organization only when
 * somebody deliberately adopts the recommendation. A test asserts the list is
 * complete, so adding a recommendation that departs from current behaviour
 * cannot pass unnoticed.
 */
export function legacyDiffersFromRecommended(): Array<{ section: GovernedSection; key: string }> {
  const out: Array<{ section: GovernedSection; key: string }> = [];
  for (const section of GOVERNED_SECTIONS) {
    for (const key of GOVERNED_KEYS[section]) {
      if (!(key in LEGACY[section])) continue;
      if (!sameSetting(LEGACY[section][key], RECOMMENDED[section][key])) out.push({ section, key });
    }
  }
  return out;
}

/** Value equality for a setting: booleans, numbers, and arrays in order. */
export function sameSetting(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((v, i) => v === b[i]);
  }
  return a === b;
}

// ============================================================================
// SETTINGS VERSION
// ============================================================================

/**
 * Which KEY SET a settings document was initialized against.
 *
 * This is not protection against a default moving — explicit stored values are
 * that, and they need no version. Its one job is the case explicit values
 * cannot answer: a future release adds a governance setting, and an
 * organization initialized before it has no value for that key and no previous
 * behaviour to preserve. The version says which organizations are behind, so
 * the initializer can fill only the new key, explicitly and audibly, rather
 * than leaving it to an absence nobody notices.
 *
 * BUMP THIS whenever a key is added to or removed from GOVERNED_KEYS. A test
 * compares the key set against a snapshot and fails the build if you forget,
 * because an unbumped version means existing organizations silently never
 * receive the new setting.
 */
export const SETTINGS_VERSION = 1;

// ============================================================================
// READING
// ============================================================================

/**
 * The effective values of a section: what is stored, with LEGACY filling any
 * gap. Fields that are not governed keys are dropped, so plumbing never
 * reaches a caller that asked about governance.
 *
 * The fallback is LEGACY rather than RECOMMENDED on purpose. Between the
 * deploy and the one-time initializer every organization has an empty section,
 * and falling back to the recommendation would apply a new default to every
 * existing customer at deploy time, which is the precise thing this design
 * refuses to do.
 */
/**
 * A STORED VALUE OF THE WRONG SHAPE IS IGNORED, NOT HANDED ON.
 *
 * `updateGovernanceSection` validates everything it writes and the Firestore
 * rules refuse a client write to these documents, so this should never fire.
 * It fires anyway, because the cost of being wrong is not symmetrical: a role
 * set that is a string makes `hasAny` refuse everybody, and a reminder
 * schedule that is not an array throws on `.includes` inside a scheduled job
 * that runs for every hospital in turn. That exact fault once stopped review
 * reminders for every organization after the bad one in the iteration.
 *
 * Falling back to LEGACY means a malformed value behaves as the product did
 * before the setting existed, which is the safe direction for a visibility
 * setting and the working direction for a schedule.
 */
function storedValueIsMalformed(section: GovernedSection, key: string, value: unknown): boolean {
  if (section === 'visibility') return invalidRoleSet(value) !== null;
  if (key === 'reviewReminderDays') return invalidReminderDays(value) !== null;
  const expected = typeof RECOMMENDED[section][key];
  return typeof value !== expected;
}

export function effectiveSection(
  section: GovernedSection,
  stored: Record<string, unknown> | null | undefined,
  orgDocValues?: Record<string, unknown>,
): SectionValues {
  const out: SectionValues = {};
  for (const key of GOVERNED_KEYS[section]) {
    const fromOrgDoc = ORG_DOCUMENT_KEYS[key] ? orgDocValues?.[key] : undefined;
    const raw = fromOrgDoc !== undefined ? fromOrgDoc : stored?.[key];
    if (raw !== undefined && raw !== null && !storedValueIsMalformed(section, key, raw)) {
      out[key] = raw as SettingValue;
      continue;
    }
    // `remindersEnabled` has no legacy constant: absent means off, which is
    // exactly what orgRemindersEnabled() has always said. Every other key has
    // one, and falls back to the recommendation only if somebody adds a key
    // to GOVERNED_KEYS and forgets to give it a legacy value, which
    // legacyCovers.test.ts refuses.
    const fallback = LEGACY[section][key] ?? (key === 'remindersEnabled' ? false : RECOMMENDED[section][key]);
    out[key] = fallback ?? false;
  }
  return out;
}

/** Whether this section still needs the one-time initialization. */
export function needsInitialization(stored: Record<string, unknown> | null | undefined): boolean {
  if (!stored) return true;
  const v = stored.settingsVersion;
  return typeof v !== 'number' || v < SETTINGS_VERSION;
}

// ============================================================================
// THE ANSWERS THE PRODUCT ASKS
// ============================================================================

export interface VisibilitySettings {
  approvalRecordViewRoles: string[];
  approvalCommentViewRoles: string[];
  committeeEvidenceViewRoles: string[];
  versionHistoryViewRoles: string[];
  retiredPolicyViewRoles: string[];
  acknowledgementReportViewRoles: string[];
}

/** One read of the settings, one answer per question. */
export function visibilityFor(roles: readonly string[] | null | undefined, v: VisibilitySettings) {
  return {
    approvalRecord: mayView(roles, v.approvalRecordViewRoles),
    approvalComments: mayView(roles, v.approvalCommentViewRoles),
    committeeEvidence: mayView(roles, v.committeeEvidenceViewRoles),
    versionHistory: mayView(roles, v.versionHistoryViewRoles),
    retiredPolicies: mayView(roles, v.retiredPolicyViewRoles),
    acknowledgementReports: mayView(roles, v.acknowledgementReportViewRoles),
  };
}

// ============================================================================
// VALIDATION
// ============================================================================

/** Bounds mirrored by the zod schemas, so the page and the callable agree. */
export const BOUNDS = {
  reviewReminderDays: { maxEntries: 6, min: 1, max: 365 },
  stalledApprovalAfterDays: { min: 1, max: 365 },
  defaultReviewIntervalMonths: { min: 1, max: 120 },
  defaultAcknowledgementDays: { min: 1, max: 365 },
  escalationAfterDaysLate: { min: 1, max: 365 },
} as const;

/** A role set is valid when it is a non-empty subset of the settable roles. */
export function invalidRoleSet(value: unknown): string | null {
  if (!Array.isArray(value)) return 'must be a list of roles';
  if (value.length === 0) return 'must name at least one role';
  if (value.length > SETTABLE_ROLES.length) return 'names more roles than exist';
  const seen = new Set<string>();
  for (const r of value) {
    if (typeof r !== 'string') return 'must be a list of role names';
    if (r === 'sysadmin') return 'System Admin always has access and is not listed';
    if (!(SETTABLE_ROLES as readonly string[]).includes(r)) return `"${r}" is not a role`;
    if (seen.has(r)) return `"${r}" is listed twice`;
    seen.add(r);
  }
  return null;
}

/** Descending, bounded, and no more entries than a person would ever set. */
export function invalidReminderDays(value: unknown): string | null {
  const b = BOUNDS.reviewReminderDays;
  if (!Array.isArray(value)) return 'must be a list of days';
  if (value.length === 0) return 'must name at least one day';
  if (value.length > b.maxEntries) return `cannot have more than ${b.maxEntries} reminders`;
  let previous = Number.POSITIVE_INFINITY;
  for (const d of value) {
    if (typeof d !== 'number' || !Number.isInteger(d)) return 'must be whole numbers of days';
    if (d < b.min || d > b.max) return `each reminder must be between ${b.min} and ${b.max} days`;
    if (d >= previous) return 'must count down, furthest ahead first';
    previous = d;
  }
  return null;
}

// ============================================================================
// WHAT IS NOT CONFIGURABLE, STATED WHERE PEOPLE LOOK FOR SETTINGS
// ============================================================================

/**
 * Shown on the settings page under "StandingDocs safety rules". Not a list of
 * disabled switches: a short statement of what an organization does not get to
 * decide, so nobody goes looking for the switch.
 */
export const SAFETY_RULES: string[] = [
  'A policy must complete its approval before it can be published. There is no way to publish around a workflow.',
  'One organization can never read another organization’s documents, members or evidence.',
  'Published versions, approval decisions and acknowledgements are never rewritten, including when these settings change.',
  'An acknowledgement can only be recorded against the version a person was actually asked to read.',
  'Drafts and revisions in progress stay closed to readers whatever the visibility settings say.',
  'Every governance change records who made it, when, and what it was before.',
  'Your System Admin always keeps access to these settings, and always needs a second factor to use them.',
];

// ============================================================================
// WORDS
// ============================================================================

/**
 * Role names as a hospital administrator reads them, plural, because every
 * sentence these appear in is about a group of people.
 */
export const ROLE_PLURAL: Record<SettableRole, string> = {
  viewer: 'Readers',
  author: 'Authors',
  reviewer: 'Reviewers',
  approver: 'Approvers',
  admin: 'Administrators',
};

/** "Authors, Reviewers, Approvers and Administrators". */
export function describeRoles(roles: readonly string[] | null | undefined): string {
  if (!Array.isArray(roles) || roles.length === 0) return 'nobody';
  const named = SETTABLE_ROLES.filter((r) => roles.includes(r)).map((r) => ROLE_PLURAL[r]);
  if (named.length === 0) return 'nobody';
  if (named.length === 1) return named[0] as string;
  const last = named[named.length - 1] as string;
  return `${named.slice(0, -1).join(', ')} and ${last}`;
}

export interface SettingLabel {
  label: string;
  help: string;
  /** Which of the page's groups it belongs to. */
  group: 'approvalEvidence' | 'library' | 'reminders' | 'reminderTiming' | 'publishing';
}

/**
 * What every governed setting is called, and why somebody would change it.
 * No internal field name ever reaches a screen, and the audit log borrows the
 * same words so the record and the page do not describe a change differently.
 */
export const SETTING_LABELS: Record<GovernedSection, Record<string, SettingLabel>> = {
  visibility: {
    approvalRecordViewRoles: {
      label: 'Approval record',
      help: 'Who may see how a published policy was approved: the workflow it followed, the sign-offs it needed, and who decided what.',
      group: 'approvalEvidence',
    },
    approvalCommentViewRoles: {
      label: 'Approval comments',
      help: 'Who may read the comments people wrote while a policy was being reviewed. Everyone who can see the approval record still sees who decided what and when.',
      group: 'approvalEvidence',
    },
    committeeEvidenceViewRoles: {
      label: 'Committee evidence',
      help: 'Who may see the meeting date, the vote and the minutes reference recorded when a committee decided.',
      group: 'approvalEvidence',
    },
    versionHistoryViewRoles: {
      label: 'Earlier versions',
      help: 'Who may open a superseded version of a policy to see what was in force on a past date.',
      group: 'library',
    },
    retiredPolicyViewRoles: {
      label: 'Retired policies',
      help: 'Who may browse policies your organization has retired. Retired policies never appear in search results.',
      group: 'library',
    },
    acknowledgementReportViewRoles: {
      label: 'Who has acknowledged',
      help: 'Who may see the list of staff who have and have not acknowledged a policy, and export it. Everybody always sees their own.',
      group: 'library',
    },
  },
  notifications: {
    remindersEnabled: {
      label: 'Send reminder email to staff',
      help: 'The switch for all reminder email from this organization. With it off, nothing below is sent.',
      group: 'reminders',
    },
    acknowledgementRemindersEnabled: {
      label: 'Acknowledgement reminders',
      help: 'Chase staff who have been asked to read a policy and have not confirmed it.',
      group: 'reminders',
    },
    reviewRemindersEnabled: {
      label: 'Review reminders',
      help: 'Tell policy owners a review is coming, and tell administrators when one is overdue.',
      group: 'reminders',
    },
    stalledApprovalDigestEnabled: {
      label: 'Stalled approval digest',
      help: 'A Monday summary of approvals that have sat on the same step too long.',
      group: 'reminders',
    },
    acknowledgementRemindersMandatory: {
      label: 'Staff may switch off acknowledgement reminders',
      help: 'Switching a reminder off never removes the obligation. The task stays, and it still goes overdue.',
      group: 'reminders',
    },
    reviewRemindersMandatory: {
      label: 'Staff may switch off review reminders',
      help: 'Applies to the owners of policies and to administrators.',
      group: 'reminders',
    },
    stalledApprovalDigestMandatory: {
      label: 'Staff may switch off the stalled approval digest',
      help: 'Applies to administrators and to the people who submitted the documents.',
      group: 'reminders',
    },
    reviewReminderDays: {
      label: 'Warn owners this far ahead',
      help: 'How many days before a review is due an owner is told. Counting down, furthest ahead first.',
      group: 'reminderTiming',
    },
    stalledApprovalAfterDays: {
      label: 'Call an approval stalled after',
      help: 'How long an approval may sit on the same step before it appears in the Monday digest.',
      group: 'reminderTiming',
    },
  },
  governance: {
    defaultReviewIntervalMonths: {
      label: 'Default review interval',
      help: 'Applied to a policy that does not set its own.',
      group: 'publishing',
    },
    enforceReviewDate: {
      label: 'Require a review date before publishing',
      help: 'A policy with no review date can never become overdue, which is how a library quietly goes stale.',
      group: 'publishing',
    },
    requireOwnerOnPublish: {
      label: 'Require an owner before publishing',
      help: 'An unowned policy has nobody to chase when its review falls due. Imported libraries often have policies with no owner.',
      group: 'publishing',
    },
    requirePolicyNumberOnPublish: {
      label: 'Require a policy number before publishing',
      help: 'A surveyor asks for documents by number.',
      group: 'publishing',
    },
  },
  acknowledgements: {
    acknowledgementsEnabled: {
      label: 'Ask staff to acknowledge policies',
      help: 'Turn this off and no new acknowledgement is requested. Acknowledgements already recorded are kept.',
      group: 'publishing',
    },
    defaultAcknowledgementDays: {
      label: 'Days to acknowledge',
      help: 'The deadline given when a policy is published, unless the policy sets its own.',
      group: 'publishing',
    },
    escalationEnabled: {
      label: 'Tell administrators about overdue reviews',
      help: 'Email administrators when a policy has passed its review date.',
      group: 'publishing',
    },
    escalationAfterDaysLate: {
      label: 'Tell them after',
      help: 'How long past the review date to wait before the first escalation.',
      group: 'publishing',
    },
  },
};

/** One value, in words. Used on the page and in the audit entry alike. */
export function describeValue(section: GovernedSection, key: string, value: unknown): string {
  if (section === 'visibility') return describeRoles(value as string[]);
  if (key === 'reviewReminderDays') {
    const days = Array.isArray(value) ? value : [];
    return days.length ? `${days.join(', ')} days ahead` : 'never';
  }
  if (key.endsWith('Mandatory')) return value === true ? 'no' : 'yes';
  if (typeof value === 'boolean') return value ? 'on' : 'off';
  if (key === 'defaultReviewIntervalMonths') return `${value} months`;
  if (typeof value === 'number') return `${value} days`;
  return String(value);
}

/** A change, in one sentence, for the audit log and the confirmation dialog. */
export function describeChange(section: GovernedSection, key: string, previous: unknown, next: unknown): string {
  const label = SETTING_LABELS[section]?.[key]?.label ?? key;
  return `${label}: ${describeValue(section, key, previous)} to ${describeValue(section, key, next)}`;
}
